import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import { SyncLoader } from "react-spinners";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/system";

import { useErrorSnackbar } from "../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAppcuesEventTrack, useInsights } from "./hooks";
import { type Filters, type Insight, type InsightsSortOrder, type SavingsPeriod } from "./types";
import { getInsightId } from "./utils";

type InsightsContext = {
  savingsPeriod: SavingsPeriod;
  selectedInsight: Insight | null;
  setSelectedInsight: Dispatch<SetStateAction<Insight | null>>;
  lastSelectedInsightId: string;
  setLastSelectedInsightId: Dispatch<SetStateAction<string>>;
  scrolledToLastSelectedInsight: boolean;
  setScrolledToLastSelectedInsight: Dispatch<SetStateAction<boolean>>;
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  sortOrder: InsightsSortOrder | "";
  setSortOrder: Dispatch<SetStateAction<InsightsSortOrder | "">>;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  hasAppcuesCloudFlowAnnouncement?: boolean;
};

export const InsightsContext = createContext({} as InsightsContext);

type Parameters = {
  providerId: string;
  insightKey: string;
};

const Loader = ({ color }: { color?: string }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100%",
    }}
  >
    <SyncLoader size={10} color={color} loading />
  </Box>
);

export function InsightsContextProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { providerId, insightKey } = useParams<Parameters>();

  const { insights, isLoading, error } = useInsights();

  const errSnackbar = useErrorSnackbar(10);

  // TODO: Remove useAppcuesEventTrack after Appcues modal has served it's purpose: [CMP-29055]
  const appcuesCloudFlowAnnouncementFlow = useAppcuesEventTrack("CloudFlow Insights Integration Announcement");

  const hasAppcuesCloudFlowAnnouncement = !!appcuesCloudFlowAnnouncementFlow;

  const theme = useTheme();

  const savingsPeriod: SavingsPeriod = "yearly";

  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [lastSelectedInsightId, setLastSelectedInsightId] = useState<string>("");
  const [scrolledToLastSelectedInsight, setScrolledToLastSelectedInsight] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filters>({
    providers: [],
    categories: [],
    status: [],
    priority: [],
    tags: [],
    additionalOptions: [],
  });
  const [sortOrder, setSortOrder] = useState<InsightsSortOrder | "">("");
  const [searchTerm, setSearchTerm] = useState("");

  // Reset selectedInsight whenever the providerId or insightKey change
  // This ensures we never return an outdated insight for the old params.
  useEffect(() => {
    setSelectedInsight(null);
  }, [providerId, insightKey]);

  useEffect(() => {
    if (isLoading || !(providerId && insightKey) || !insights) {
      return;
    }

    const matchingInsight = (insights || []).find(
      (insight) => insight.providerId === providerId && insight.key === insightKey
    );

    setSelectedInsight(matchingInsight || null);
    setLastSelectedInsightId(matchingInsight ? getInsightId(matchingInsight) : "");
    setScrolledToLastSelectedInsight(false);
  }, [insights, isLoading, providerId, insightKey]);

  useEffect(() => {
    if (!error) {
      return;
    }
    errSnackbar(error.message || "Failed to load insights");
  }, [error, errSnackbar]);

  const contextValue = useMemo(
    () => ({
      savingsPeriod,
      selectedInsight,
      setSelectedInsight,
      lastSelectedInsightId,
      setLastSelectedInsightId,
      scrolledToLastSelectedInsight,
      setScrolledToLastSelectedInsight,
      filters,
      setFilters,
      sortOrder,
      setSortOrder,
      searchTerm,
      setSearchTerm,
      hasAppcuesCloudFlowAnnouncement,
    }),
    [
      savingsPeriod,
      selectedInsight,
      lastSelectedInsightId,
      scrolledToLastSelectedInsight,
      filters,
      sortOrder,
      searchTerm,
      hasAppcuesCloudFlowAnnouncement,
    ]
  );

  if (isLoading) {
    return <Loader color={theme.palette.primary.main} />;
  }

  return <InsightsContext.Provider value={contextValue}>{children}</InsightsContext.Provider>;
}

export const useInsightsContext = () => useContext(InsightsContext);
